import React from 'react';
import { i18n } from 'config/locales';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError, CommonOnUpdate, CommonRequired, CommonScrollIntoView
} from 'types/props';
import { ForeignBornDocumentModel } from 'forms/shared/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import ValidationElement from '../ValidationElement';
import { alphaNumericRegEx } from '../../../validators/helpers';
import RadioGroup from '../RadioGroup';
import Radio from '../Radio';
import Textarea from '../Textarea';
import Text from '../Text';
import Field from '../Field';
import DateControl from '../DateControl';
import NotApplicable from '../NotApplicable';
import Branch from '../Branch';
import Checkbox from '../Checkbox';

// CLEANUP: COPIED FOREIGNBORNDOC, ONLY PVQ
// TODO: replicate Status in citizenship
interface Props extends Partial<ForeignBornDocumentModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  requireExpirationDate?: boolean
  attribute?: string
  formType?: string
}

export default class ForeignBornDocumentsCivil extends ValidationElement<Props> {
  // TODO: bad tech debt, communicate on where to go with these getters
  getPvqCitizenshipStatus = () => [
    {
      name: 'us-citizen',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.usCitizen'),
      value: 'US birth',
    },
    {
      name: 'us-foreign',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.usForeign'),
      value: 'US Foreign',
    },
    {
      name: 'naturalized',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.naturalized'),
      value: 'Naturalized',
    },
    {
      name: 'derived',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.derived'),
      value: 'Derived',
    },
    {
      name: 'not-us',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.notUs'),
      value: 'Not US',
    },
    {
      name: 'national-by-birth-in-us',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.nationalByBirthInUs'),
      value: 'National By Birth In US',
    },
    {
      name: 'national-by-birth-outside-us',
      label: i18n.m('relationships.pvq.relatives.label.citizenshipStatus.nationalByBirthOutsideUS'),
      value: 'National By Birth Outside US',
    },
  ];

  getPvqUsForeignDocumentation = () => [
    {
      name: 'us-foreign-fs240',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.fs240'),
      value: 'FS240',
    },
    {
      name: 'us-foreign-ds1350',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.ds1350'),
      value: 'DS1350',
    },
    {
      name: 'us-foreign-fs545',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.fs545'),
      value: 'FS545',
    },
    {
      name: 'us-foreign-n560',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.n560'),
      value: 'N560',
    },
    {
      name: 'us-foreign-other',
      label: i18n.m('relationships.pvq.relatives.label.usForeign.other'),
      value: 'Other',
    }
  ];

  getPvqNaturalizedDocumentation = () => [
    {
      name: 'naturalized-certificate',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.certificate'),
      value: 'NaturalizedCertificate',
    },
    {
      name: 'naturalized-permanent-alien',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.permanentAlien'),
      value: 'NaturalizedPermanentAlien',
    },
    {
      name: 'naturalized-mriv',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.mriv'),
      value: 'NaturalizedMriv',
    },
    {
      name: 'naturalized-other',
      label: i18n.m('relationships.pvq.relatives.label.naturalized.other'),
      value: 'Other',
    },
  ];

  getPvqDerivedDocumentation = () => [
    {
      name: 'derived-certificate',
      label: i18n.m('relationships.pvq.relatives.label.derived.certificate'),
      value: 'DerivedCertificate',
    },
    {
      name: 'derived-permanent-alien',
      label: i18n.m('relationships.pvq.relatives.label.derived.permanentAlien'),
      value: 'DerivedPermanentAlien',
    },
    {
      name: 'derived-mriv',
      label: i18n.m('relationships.pvq.relatives.label.derived.mriv'),
      value: 'DerivedMriv',
    },
    {
      name: 'derived-other',
      label: i18n.m('relationships.pvq.relatives.label.derived.other'),
      value: 'Other',
    },
  ];

  getPvqResidency = () => [
    {
      name: 'residency-permanent',
      label: i18n.m('relationships.pvq.relatives.label.residency.permanent'),
      value: 'ResidencyPermanent',
    },
    {
      name: 'residency-permanent-alien',
      label: i18n.m('relationships.pvq.relatives.label.residency.asylee'),
      value: 'ResidencyAsylee',
    },
    {
      name: 'residency-mriv',
      label: i18n.m('relationships.pvq.relatives.label.residency.nonimmigrant'),
      value: 'ResidencyNonimmigrant',
    },
    {
      name: 'residency-tps',
      label: i18n.m('relationships.pvq.relatives.label.residency.tps'),
      value: 'ResidencyTPS',
    },
    {
      name: 'residency-daca',
      label: i18n.m('relationships.pvq.relatives.label.residency.daca'),
      value: 'ResidencyDACA',
    },
    {
      name: 'residency-other',
      label: i18n.m('relationships.pvq.relatives.label.residency.other'),
      value: 'Other',
    },
  ];

  getPvqNonCitizenDocumentation = () => [
    {
      name: 'document-permanent',
      label: i18n.m('relationships.pvq.relatives.label.document.permanent'),
      value: 'NonPermanent',
    },
    {
      name: 'document-employment',
      label: i18n.m('relationships.pvq.relatives.label.document.employment'),
      value: 'NonEmployment',
    },
    {
      name: 'document-mriv',
      label: i18n.m('relationships.pvq.relatives.label.document.mriv'),
      value: 'NonMRIV',
    },
    {
      name: 'document-arrival',
      label: i18n.m('relationships.pvq.relatives.label.document.arrival'),
      value: 'NonArrival',
    },
    {
      name: 'document-visa',
      label: i18n.m('relationships.pvq.relatives.label.document.visa'),
      value: 'NonVisa',
    },
    {
      name: 'document-f1',
      label: i18n.m('relationships.pvq.relatives.label.document.f1'),
      value: 'NonF1',
    },
    {
      name: 'document-j1',
      label: i18n.m('relationships.pvq.relatives.label.document.j1'),
      value: 'NonJ1',
    },
    {
      name: 'document-other',
      label: i18n.m('relationships.pvq.relatives.label.document.other'),
      value: 'Other',
    },
  ];

  update = (queue: Partial<ForeignBornDocumentModel>) => {
    this.props.onUpdate({
      DocumentType: this.props.DocumentType,
      ResidenceType: this.props.ResidenceType,
      CitizenType: this.props.CitizenType,
      OtherExplanation: this.props.OtherExplanation,
      DocumentNumber: this.props.DocumentNumber,
      DocumentNumberNotApplicable: this.props.DocumentNumberNotApplicable,
      NoCertificateNumber: this.props.NoCertificateNumber,
      CertificateNumber: this.props.CertificateNumber,
      CertificateNumberNotApplicable: this.props.CertificateNumberNotApplicable,
      DocumentExpiration: this.props.DocumentExpiration,
      DocumentExpirationNotApplicable: this.props.DocumentExpirationNotApplicable,
      DocumentExpirationUnknown: this.props.DocumentExpirationUnknown,
      Comments: this.props.Comments,
      LivesInUnitedStates: this.props.LivesInUnitedStates,
      ResidenceTypeOtherExplanation: this.props.ResidenceTypeOtherExplanation,
      ...queue,
    });
  };

  updateDocumentType = (values: ForeignBornDocumentModel['DocumentType']) => {
    this.update({
      DocumentType: values,
      DocumentExpirationNotApplicable: { applicable: true },
      DocumentNumber: {},
      DocumentNumberNotApplicable: { applicable: true },
      CertificateNumber: {},
      OtherExplanation: {},
      CertificateNumberNotApplicable: { applicable: true },
    });
  };

  updateResidenceType = (values: ForeignBornDocumentModel['ResidenceType']) => {
    this.update({
      ResidenceType: values,
      ResidenceTypeOtherExplanation: values?.value === 'Other' ? this.props.ResidenceTypeOtherExplanation : {},
    });
  };

  updateCitizenType = (values: ForeignBornDocumentModel['CitizenType']) => {
    this.update({
      CitizenType: values,
      DocumentType: {},
      DocumentNumber: {},
      DocumentNumberNotApplicable: { applicable: true },
      NoCertificateNumber: {},
      DocumentExpirationNotApplicable: { applicable: true },
      CertificateNumber: {},
      CertificateNumberNotApplicable: { applicable: true },
      ResidenceType: {},
      LivesInUnitedStates: {},
    });
  };

  updateOtherExplanation = (values: ForeignBornDocumentModel['OtherExplanation']) => {
    this.update({
      OtherExplanation: values,
    });
  };

  updateDocumentNumber = (values: ForeignBornDocumentModel['DocumentNumber']) => {
    this.update({
      DocumentNumber: values,
    });
  };

  updateDocumentNumberNotApplicable = (
    values: ForeignBornDocumentModel['DocumentNumberNotApplicable']
  ) => {
    this.update({
      DocumentNumberNotApplicable: values,
      DocumentNumber: {},
    });
  };

  updateNoCertificateNumber = (value: ForeignBornDocumentModel['NoCertificateNumber']) => {
    const hasNoCertificateNumber = value?.checked === true;

    this.update({
      CertificateNumber: hasNoCertificateNumber ? {} : this.props.CertificateNumber,
      NoCertificateNumber: value
    });
  };

  updateResidenceTypeOtherExplanation = (values: ForeignBornDocumentModel['ResidenceTypeOtherExplanation']) => {
    this.update({
      ResidenceTypeOtherExplanation: values,
    });
  };

  updateCertificateNumber = (values: ForeignBornDocumentModel['CertificateNumber']) => {
    this.update({
      CertificateNumber: values,
    });
  };

  updateCertificateNumberNotApplicable = (values: ForeignBornDocumentModel['CertificateNumberNotApplicable']) => {
    this.update({
      CertificateNumberNotApplicable: values,
      CertificateNumber: {},
    });
  };

  updateDocumentExpiration = (values: ForeignBornDocumentModel['DocumentExpiration']) => {
    this.update({
      DocumentExpiration: values,
    });
  };

  updateDocumentExpirationNotApplicable = (
    values: ForeignBornDocumentModel['DocumentExpirationNotApplicable']
  ) => {
    this.update({
      DocumentExpirationNotApplicable: values,
      DocumentExpirationUnknown: {},
      DocumentExpiration: {},
    });
  };

  updateDocumentExpirationUnknown = (
    values: ForeignBornDocumentModel['DocumentExpirationUnknown']
  ) => {
    this.update({
      DocumentExpirationUnknown: values,
      DocumentExpirationNotApplicable: {},
      DocumentExpiration: {},
    });
  };

  updateComments = (values: ForeignBornDocumentModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateLivesInUnitedStates = (values) => {
    if (values.value === 'No') {
      this.update({
        LivesInUnitedStates: values,
        ResidenceType: {},
        DocumentNumber: {},
        DocumentNumberNotApplicable: { applicable: true },
        DocumentType: {},
        DocumentExpiration: {},
        DocumentExpirationNotApplicable: { applicable: true },
      });
    } else {
      this.update({
        LivesInUnitedStates: values,
      });
    }
  };

  render() {
    const { attribute } = this.props;

    const isNotUsCitizen = (
      this.props.CitizenType?.value === 'Not US'
      || this.props.CitizenType?.value === 'National By Birth In US'
      || this.props.CitizenType?.value === 'National By Birth Outside US'
    );

    const showOtherDocumentation = () => {
      const isOtherDocumentType = this.props.DocumentType?.value === 'Other';
      const livesInUs = this.props.LivesInUnitedStates?.value === 'Yes';
      const isUsCitizenNotByBirth = this.props.CitizenType?.value !== 'US birth' && !isNotUsCitizen;

      return isOtherDocumentType && ((isNotUsCitizen && livesInUs) || isUsCitizenNotByBirth);
    };

    const showDocumentExpiration = () => {
      const livesInUs = this.props.LivesInUnitedStates?.value === 'Yes';
      const isUsCitizenNotByBirth = this.props.CitizenType?.value !== 'US birth' && !isNotUsCitizen;

      return (isNotUsCitizen && livesInUs) || isUsCitizenNotByBirth;
    };

    return (
      <div className="foreign-born-documents">
        <Field
          help="relationships.civilUnion.help.foreignBornDocument"
          title={i18n.t('pvqForeignBornDocumentsCivil.heading.citizenshipStatus')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="p"
          attribute={`${attribute}.CitizenType`}
        >
          <RadioGroup
            name="citstat"
            className="option-list option-list-vertical"
            selectedValue={(this.props.CitizenType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.CitizenType`}
          >
            {this.getPvqCitizenshipStatus().map((document) => (
              <Radio
                key={document.name}
                name={document.name}
                label={document.label}
                value={document.value}
                className={document.name}
                onError={this.props.onError}
                onUpdate={this.updateCitizenType}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.CitizenType?.value === 'US Foreign' && (
          <div>
            <Field
              title={i18n.t('pvqForeignBornDocumentsCivil.para.bornToUSParents')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="p"
              attribute={`${attribute}.DocumentType`}
            >
              <RadioGroup
                name="born"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                {this.getPvqUsForeignDocumentation().map((document) => (
                  <Radio
                    key={document.name}
                    name={document.name}
                    label={document.label}
                    value={document.value}
                    className={document.name}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentType}
                  />
                ))}
              </RadioGroup>
            </Field>
          </div>
        )}
        {this.props.CitizenType?.value === 'US Foreign'
          && this.props.DocumentType?.value !== 'Other' && (
            <div>
              <Field
                title={i18n.t('pvqForeignBornDocumentsCivil.heading.serialNumber')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="labels"
                attribute={`${attribute}.DocumentNumber`}
              >
                <NotApplicable
                  name="DocumentNumberNotApplicable"
                  {...this.props.DocumentNumberNotApplicable}
                  label={i18n.t('reference.pvq.label.idkNumber')}
                  or={i18n.m('reference.para.or')}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                >
                  <Text
                    name="DocumentNumber"
                    {...this.props.DocumentNumber}
                    className="foreign-born-document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    onUpdate={this.updateDocumentNumber}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.DocumentNumber`}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
        {this.props.CitizenType?.value === 'Naturalized' && (
          <div>
            <Field
              title={i18n.t('pvqForeignBornDocumentsCivil.para.naturalized')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="p"
              attribute={`${attribute}.DocumentType`}
            >
              <RadioGroup
                name="naturalized"
                className="option-list option-list-vertical"
                selectedValue={(this.props.DocumentType || {}).value}
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
              >
                {this.getPvqNaturalizedDocumentation().map((document) => (
                  <Radio
                    key={document.name}
                    name={document.name}
                    label={document.label}
                    value={document.value}
                    className={document.name}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentType}
                  />
                ))}
              </RadioGroup>
            </Field>
          </div>
        )}
        {(this.props.CitizenType?.value === 'Naturalized'
          && this.props.DocumentType?.value === 'NaturalizedCertificate') && (
            <div>
              <Field
                title={i18n.t('pvqForeignBornDocumentsCivil.naturalized.certificate.alien')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="labels"
                attribute={`${attribute}.DocumentNumber`}
              >
                <NotApplicable
                  name="DocumentNumberNotApplicable"
                  {...this.props.DocumentNumberNotApplicable}
                  label={i18n.t('reference.label.idk')}
                  or={i18n.m('reference.para.or')}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                >
                  <Text
                    name="USAlienCertificate"
                    label={i18n.t('pvqForeignBornDocumentsCivil.naturalized.certificate.alienLabel')}
                    {...this.props.DocumentNumber}
                    className="foreign-born-document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    onUpdate={this.updateDocumentNumber}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.DocumentNumber`}
                  />
                </NotApplicable>
              </Field>
              <Field
                title={i18n.t('pvqForeignBornDocumentsCivil.naturalized.certificate.naturalization')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="labels"
                attribute={`${attribute}.CertificateNumber`}
              >
                <NotApplicable
                  name="CertificateNumberNotApplicable"
                  {...this.props.CertificateNumberNotApplicable}
                  label={i18n.t('reference.label.idk')}
                  or={i18n.m('reference.para.or')}
                  onUpdate={this.updateCertificateNumberNotApplicable}
                  attribute={`${attribute}.CertificateNumberNotApplicable`}
                >
                  <Text
                    name="CertificateNumber"
                    label={i18n.t('pvqForeignBornDocumentsCivil.naturalized.certificate.naturalizationLabel')}
                    {...this.props.CertificateNumber}
                    className="foreign-born-document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    onUpdate={this.updateCertificateNumber}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.CertificateNumber`}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
        {(this.props.CitizenType?.value === 'Naturalized')
          && (this.props.DocumentType?.value === 'NaturalizedPermanentAlien'
            || this.props.DocumentType?.value === 'NaturalizedMriv') && (
            <div>
              <Field
                title={i18n.t('pvqForeignBornDocumentsCivil.naturalized.permanent.alien')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="labels"
                attribute={`${attribute}.DocumentNumber`}
              >
                <NotApplicable
                  name="DocumentNumberNotApplicable"
                  {...this.props.DocumentNumberNotApplicable}
                  label={i18n.t('reference.label.idk')}
                  or={i18n.m('reference.para.or')}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                >
                  <Text
                    name="USAlienPermanentMriv"
                    label={i18n.t('pvqForeignBornDocumentsCivil.naturalized.permanent.alienLabel')}
                    {...this.props.DocumentNumber}
                    className="foreign-born-document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    onUpdate={this.updateDocumentNumber}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.DocumentNumber`}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
        {(this.props.CitizenType?.value === 'Derived') && (
          <div>
            <Field
              title={i18n.t('pvqForeignBornDocumentsCivil.para.derived')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="p"
              attribute={`${attribute}.DocumentType`}
            >
              <RadioGroup
                className="relative-derived option-list option-list-vertical"
                required={this.props.required}
                onError={this.props.onError}
                attribute={`${attribute}.DocumentType`}
                selectedValue={(this.props.DocumentType || {}).value}
              >
                {this.getPvqDerivedDocumentation().map((document) => (
                  <Radio
                    key={document.name}
                    name={document.name}
                    label={document.label}
                    value={document.value}
                    className={document.name}
                    onError={this.props.onError}
                    onUpdate={this.updateDocumentType}
                  />
                ))}
              </RadioGroup>
            </Field>
            {(this.props.CitizenType?.value === 'Derived'
              && this.props.DocumentType?.value === 'DerivedCertificate') && (
                <div>
                  <Field
                    title={i18n.t('pvqForeignBornDocumentsCivil.derived.certificate.alien')}
                    scrollIntoView={this.props.scrollIntoView}
                    adjustFor="labels"
                    attribute={`${attribute}.DocumentNumber`}
                  >
                    <NotApplicable
                      name="DocumentNumberNotApplicable"
                      {...this.props.DocumentNumberNotApplicable}
                      label={i18n.t('reference.label.idk')}
                      or={i18n.m('reference.para.or')}
                      onUpdate={this.updateDocumentNumberNotApplicable}
                      attribute={`${attribute}.DocumentNumberNotApplicable`}
                    >
                      <Text
                        name="USAlienCertificateDerived"
                        label={i18n.t('pvqForeignBornDocumentsCivil.derived.certificate.alienLabel')}
                        {...this.props.DocumentNumber}
                        className="foreign-born-document-number"
                        maxlength="30"
                        pattern={alphaNumericRegEx}
                        prefix="alphanumeric"
                        onUpdate={this.updateDocumentNumber}
                        onError={this.props.onError}
                        required={this.props.required}
                        attribute={`${attribute}.DocumentNumber`}
                      />
                    </NotApplicable>
                  </Field>
                  <Field
                    title={i18n.t('pvqForeignBornDocumentsCivil.derived.certificate.citizenship')}
                    scrollIntoView={this.props.scrollIntoView}
                    adjustFor="labels"
                    attribute={`${attribute}.CertificateNumber`}
                  >
                    <NotApplicable
                      name="CertificateNumberNotApplicable"
                      {...this.props.CertificateNumberNotApplicable}
                      label={i18n.t('reference.label.idk')}
                      or={i18n.m('reference.para.or')}
                      onUpdate={this.updateCertificateNumberNotApplicable}
                      attribute={`${attribute}.CertificateNumberNotApplicable`}
                    >
                      <Text
                        name="CitizenshipNumber"
                        label={i18n.t('pvqForeignBornDocumentsCivil.derived.certificate.citizenshipLabel')}
                        {...this.props.CertificateNumber}
                        className="foreign-born-document-number"
                        maxlength="30"
                        pattern={alphaNumericRegEx}
                        prefix="alphanumeric"
                        onUpdate={this.updateCertificateNumber}
                        onError={this.props.onError}
                        required={this.props.required}
                        attribute={`${attribute}.CertificateNumber`}
                      />
                    </NotApplicable>
                  </Field>
                </div>
              )}
          </div>
        )}
        {(this.props.CitizenType?.value === 'Derived')
          && (this.props.DocumentType?.value === 'DerivedPermanentAlien'
            || this.props.DocumentType?.value === 'DerivedMriv') && (
            <div>
              <Field
                title={i18n.t('pvqForeignBornDocumentsCivil.derived.permanent.alien')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="labels"
                attribute={`${attribute}.DocumentNumber`}
              >
                <NotApplicable
                  name="DocumentNumberNotApplicable"
                  {...this.props.DocumentNumberNotApplicable}
                  label={i18n.t('reference.label.idk')}
                  or={i18n.m('reference.para.or')}
                  onUpdate={this.updateDocumentNumberNotApplicable}
                  attribute={`${attribute}.DocumentNumberNotApplicable`}
                >
                  <Text
                    name="USAlienPermanentMriv"
                    label={i18n.t('pvqForeignBornDocumentsCivil.derived.permanent.alienLabel')}
                    {...this.props.DocumentNumber}
                    className="foreign-born-document-number"
                    maxlength="30"
                    pattern={alphaNumericRegEx}
                    prefix="alphanumeric"
                    onUpdate={this.updateDocumentNumber}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.DocumentNumber`}
                  />
                </NotApplicable>
              </Field>
            </div>
          )}
        {isNotUsCitizen && (
          <div>
            <Branch
              {...this.props.LivesInUnitedStates}
              name="LivesInUnitedStates"
              attribute="LivesInUnitedStates"
              label={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.label.livesInUnitedStates')}
              labelSize="h4"
              className="LivesInUnitedStates"
              warning
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              required={this.props.required}
              onUpdate={this.updateLivesInUnitedStates}
              scrollIntoView={this.props.scrollIntoView}
            />

            {this.props.LivesInUnitedStates?.value === 'Yes' && (
              <>
                <Field
                  title={i18n.t('relationships.pvq.relatives.heading.us.notUsResidence')}
                  scrollIntoView={this.props.scrollIntoView}
                  adjustFor="p"
                  attribute={`${attribute}.ResidenceType`}
                >
                  <RadioGroup
                    name="residenceStatus"
                    className="option-list option-list-vertical"
                    selectedValue={(this.props.ResidenceType || {}).value}
                    required={this.props.required}
                    onError={this.props.onError}
                    attribute={`${attribute}.ResidenceType`}
                  >
                    {this.getPvqResidency().map((document) => (
                      <Radio
                        key={document.name}
                        name={document.name}
                        label={document.label}
                        value={document.value}
                        className={document.name}
                        onError={this.props.onError}
                        onUpdate={this.updateResidenceType}
                      />
                    ))}
                  </RadioGroup>
                  {this.props.ResidenceType?.value === 'Other' && (
                    <Field
                      title={i18n.t('relationships.pvq.relatives.heading.other')}
                      titleSize="h4"
                      attribute={`${attribute}.ResidenceTypeOtherExplanation`}
                      className="relative-citizenship-documentation no-margin-bottom"
                    >
                      <Text
                        name="ResidenceTypeOtherExplanation"
                        {...this.props.ResidenceTypeOtherExplanation}
                        className="foreign-born-document-number"
                        pattern={alphaNumericRegEx}
                        prefix="alphanumeric"
                        onUpdate={this.updateResidenceTypeOtherExplanation}
                        onError={this.props.onError}
                        required={this.props.required}
                        attribute={`${attribute}.ResidenceTypeOtherExplanation`}
                      />
                    </Field>
                  )}
                </Field>
                <Field
                  title={i18n.t('pvqForeignBornDocumentsCivil.para.notCitizen')}
                  scrollIntoView={this.props.scrollIntoView}
                  adjustFor="p"
                  attribute={`${attribute}.DocumentType`}
                >
                  <RadioGroup
                    name="notCitizen"
                    className="option-list option-list-vertical"
                    selectedValue={(this.props.DocumentType || {}).value}
                    required={this.props.required}
                    onError={this.props.onError}
                    attribute={`${attribute}.DocumentType`}
                  >
                    {this.getPvqNonCitizenDocumentation().map((document) => (
                      <Radio
                        key={document.name}
                        name={document.name}
                        label={document.label}
                        value={document.value}
                        className={document.name}
                        onError={this.props.onError}
                        onUpdate={this.updateDocumentType}
                      />
                    ))}
                  </RadioGroup>
                </Field>
                {(this.props.DocumentType?.value === 'NonPermanent'
                  || this.props.DocumentType?.value === 'NonEmployment'
                  || this.props.DocumentType?.value === 'NonMRIV')
                  && (
                    <div>
                      <Field
                        title={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.firstThree.alien')}
                        scrollIntoView={this.props.scrollIntoView}
                        adjustFor="labels"
                        attribute={`${attribute}.DocumentNumber`}
                      >
                        <NotApplicable
                          name="DocumentNumberNotApplicable"
                          {...this.props.DocumentNumberNotApplicable}
                          label={i18n.t('reference.label.idk')}
                          or={i18n.m('reference.para.or')}
                          onUpdate={this.updateDocumentNumberNotApplicable}
                          attribute={`${attribute}.DocumentNumberNotApplicable`}
                        >
                          <Text
                            name="USAlienPermanentMriv"
                            label={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.firstThree.alienLabel')}
                            {...this.props.DocumentNumber}
                            className="foreign-born-document-number"
                            maxlength="30"
                            pattern={alphaNumericRegEx}
                            prefix="alphanumeric"
                            onUpdate={this.updateDocumentNumber}
                            onError={this.props.onError}
                            required={this.props.required}
                            attribute={`${attribute}.DocumentNumber`}
                          />
                        </NotApplicable>
                      </Field>
                    </div>
                  )}
                {this.props.DocumentType?.value === 'NonArrival' && (
                  <div>
                    <Field
                      title={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.documentNumber')}
                      scrollIntoView={this.props.scrollIntoView}
                      adjustFor="labels"
                      attribute={`${attribute}.DocumentNumber`}
                    >
                      <NotApplicable
                        name="DocumentNumberNotApplicable"
                        {...this.props.DocumentNumberNotApplicable}
                        label={i18n.t('reference.label.idk')}
                        or={i18n.m('reference.para.or')}
                        onUpdate={this.updateDocumentNumberNotApplicable}
                        attribute={`${attribute}.DocumentNumberNotApplicable`}
                      >
                        <Text
                          name="USAlienPermanentMriv"
                          label={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.i94')}
                          {...this.props.DocumentNumber}
                          className="foreign-born-document-number"
                          maxlength="30"
                          pattern={alphaNumericRegEx}
                          prefix="alphanumeric"
                          onUpdate={this.updateDocumentNumber}
                          onError={this.props.onError}
                          required={this.props.required}
                          attribute={`${attribute}.DocumentNumber`}
                        />
                      </NotApplicable>
                    </Field>
                  </div>
                )}
                {this.props.DocumentType?.value === 'NonVisa' && (
                  <div>
                    <Field
                      title={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.documentNumber')}
                      scrollIntoView={this.props.scrollIntoView}
                      adjustFor="labels"
                      attribute={`${attribute}.DocumentNumber`}
                    >
                      <NotApplicable
                        name="DocumentNumberNotApplicable"
                        {...this.props.DocumentNumberNotApplicable}
                        label={i18n.t('reference.label.idk')}
                        or={i18n.m('reference.para.or')}
                        onUpdate={this.updateDocumentNumberNotApplicable}
                        attribute={`${attribute}.DocumentNumberNotApplicable`}
                      >
                        <Text
                          name="USAlienPermanentMriv"
                          label={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.visa')}
                          {...this.props.DocumentNumber}
                          className="foreign-born-document-number"
                          maxlength="30"
                          pattern={alphaNumericRegEx}
                          prefix="alphanumeric"
                          onUpdate={this.updateDocumentNumber}
                          onError={this.props.onError}
                          required={this.props.required}
                          attribute={`${attribute}.DocumentNumber`}
                        />
                      </NotApplicable>
                    </Field>
                  </div>
                )}
                {this.props.DocumentType?.value === 'NonF1' && (
                  <div>
                    <Field
                      title={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.documentNumber')}
                      scrollIntoView={this.props.scrollIntoView}
                      adjustFor="labels"
                      attribute={`${attribute}.DocumentNumber`}
                    >
                      <NotApplicable
                        name="DocumentNumberNotApplicable"
                        {...this.props.DocumentNumberNotApplicable}
                        label={i18n.t('reference.label.idk')}
                        or={i18n.m('reference.para.or')}
                        onUpdate={this.updateDocumentNumberNotApplicable}
                        attribute={`${attribute}.DocumentNumberNotApplicable`}
                      >
                        <Text
                          name="USAlienPermanentMriv"
                          label={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.i20')}
                          {...this.props.DocumentNumber}
                          className="foreign-born-document-number"
                          maxlength="30"
                          pattern={alphaNumericRegEx}
                          prefix="alphanumeric"
                          onUpdate={this.updateDocumentNumber}
                          onError={this.props.onError}
                          required={this.props.required}
                          attribute={`${attribute}.DocumentNumber`}
                        />
                      </NotApplicable>
                    </Field>
                  </div>
                )}
                {this.props.DocumentType?.value === 'NonJ1' && (
                  <div>
                    <Field
                      title={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.documentNumber')}
                      scrollIntoView={this.props.scrollIntoView}
                      adjustFor="labels"
                      attribute={`${attribute}.DocumentNumber`}
                    >
                      <NotApplicable
                        name="DocumentNumberNotApplicable"
                        {...this.props.DocumentNumberNotApplicable}
                        label={i18n.t('reference.label.idk')}
                        or={i18n.m('reference.para.or')}
                        onUpdate={this.updateDocumentNumberNotApplicable}
                        attribute={`${attribute}.DocumentNumberNotApplicable`}
                      >
                        <Text
                          name="USAlienPermanentMriv"
                          label={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.ds2019')}
                          {...this.props.DocumentNumber}
                          className="foreign-born-document-number"
                          maxlength="30"
                          pattern={alphaNumericRegEx}
                          prefix="alphanumeric"
                          onUpdate={this.updateDocumentNumber}
                          onError={this.props.onError}
                          required={this.props.required}
                          attribute={`${attribute}.DocumentNumber`}
                        />
                      </NotApplicable>
                    </Field>
                  </div>
                )}

              </>
            )}

          </div>
        )}

        {showOtherDocumentation() && (
          <div>
            <Field
              attribute={`${attribute}.OtherExplanation`}
              title={i18n.t('pvqForeignBornDocumentsCivil.notCitizen.otherLabels.title')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="p"
            >
              <Textarea
                name="otherExplanation"
                className="other-explanation"
                {...this.props.OtherExplanation}
                onUpdate={this.updateOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.OtherExplanation`}
              />
            </Field>

            <Field
              title={i18n.t('pvqForeignBornDocumentsCivil.heading.documentNumber')}
              scrollIntoView={this.props.scrollIntoView}
              adjustFor="labels"
              attribute={`${attribute}.CertificateNumber`}
            >
              <NotApplicable
                name="CertificateNumberNotApplicable"
                {...this.props.CertificateNumberNotApplicable}
                label={i18n.t('reference.pvq.label.idk')}
                or={i18n.m('reference.para.or')}
                onUpdate={this.updateCertificateNumberNotApplicable}
                attribute={`${attribute}.CertificateNumberNotApplicable`}
              >
                <Text
                  name="CertificateNumber"
                  {...this.props.CertificateNumber}
                  className="foreign-born-document-number"
                  maxlength="30"
                  pattern={alphaNumericRegEx}
                  prefix="alphanumeric"
                  onUpdate={this.updateCertificateNumber}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.CertificateNumber`}
                />

                <Checkbox
                  label={i18n.t('reference.pvq.label.noNumber')}
                  value="Yes"
                  checked={this.props.NoCertificateNumber?.checked}
                  className="No-document-number"
                  onUpdate={this.updateNoCertificateNumber}
                  onError={this.props.onError}
                />
              </NotApplicable>
            </Field>
          </div>
        )}

        {showDocumentExpiration() && (
          <Field
            title={i18n.t('pvqForeignBornDocumentsCivil.heading.documentExpiration')}
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="labels"
            attribute={`${attribute}.DocumentExpiration`}
          >
            <NotApplicable
              name="DocumentExpirationNotApplicable"
              {...this.props.DocumentExpirationNotApplicable}
              label={i18n.t('reference.pvq.label.notApplicable')}
              or={i18n.m('reference.para.or')}
              onUpdate={this.updateDocumentExpirationNotApplicable}
              attribute={`${attribute}.DocumentExpirationNotApplicable`}
            >
              <NotApplicable
                name="DocumentExpirationNotApplicable"
                {...this.props.DocumentExpirationUnknown}
                label={i18n.t('reference.label.idk')}
                or={i18n.m('reference.para.or')}
                onUpdate={this.updateDocumentExpirationUnknown}
                attribute={`${attribute}.DocumentExpirationUnknown`}
                useChangeWarning
              >
                <DateControl
                  name="documentExpiration"
                  noMaxDate
                  {...this.props.DocumentExpiration}
                  onUpdate={this.updateDocumentExpiration}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.DocumentExpiration`}
                />
              </NotApplicable>
            </NotApplicable>
          </Field>
        )}
      </div>
    );
  }
}

ForeignBornDocumentsCivil.defaultProps = {
  DocumentType: {},
  ResidenceType: {},
  CitizenType: {},
  OtherExplanation: {},
  DocumentNumber: {},
  DocumentNumberNotApplicable: { applicable: true },
  NoCertificateNumber: {},
  DocumentExpiration: {},
  DocumentExpirationNotApplicable: { applicable: true },
  DocumentExpirationUnknown: { applicable: true },
  CertificateNumber: {},
  CertificateNumberNotApplicable: { applicable: true },
  ResidenceTypeOtherExplanation: {},
  Comments: {},
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
  requireExpirationDate: true,
};
